class StateChangeProfiler {
  constructor() {
    this.mutations = [];
    this.watchers = new Map();
    this.computedDependencies = new Map();
    this.isEnabled = true;
  }

  // Profile a specific Pinia store
  profileStore(store) {
    if (!this.isEnabled) return;

    const storeId = store.$id;
    log.info(5,`🔍 Profiling store: ${storeId}`);

    // Intercept $patch method
    const originalPatch = store.$patch;
    store.$patch = (partialStateOrMutator) => {
      if (!this.isEnabled) return originalPatch.call(store, partialStateOrMutator);

      const startTime = performance.now();
      const beforeState = this.cloneState(store.$state);

      const result = originalPatch.call(store, partialStateOrMutator);

      const endTime = performance.now();
      const afterState = this.cloneState(store.$state);
      const changes = this.diffStates(beforeState, afterState);

      this.recordMutation({
        storeId,
        type: '$patch',
        changes,
        duration: endTime - startTime,
        timestamp: Date.now(),
        stack: this.getCallStack()
      });

      return result;
    };

    // Monitor state changes via proxy
    this.wrapStateProxy(store, storeId);

    // Monitor computed properties and getters
    this.monitorComputedProperties(store, storeId);
  }

  wrapStateProxy(store, storeId) {
    const originalState = store.$state;
    const self = this;

    // Check if an object should be proxied
    function shouldProxy(obj) {
      if (!obj || typeof obj !== 'object') return false;
      
      // Don't proxy built-in objects that have special internal slots
      if (obj instanceof Map || 
          obj instanceof Set || 
          obj instanceof WeakMap || 
          obj instanceof WeakSet ||
          obj instanceof Date ||
          obj instanceof RegExp ||
          obj instanceof Promise ||
          obj instanceof ArrayBuffer ||
          obj.constructor !== Object && obj.constructor !== Array) {
        return false;
      }
      
      return true;
    }

    // Create a deep proxy for nested objects
    function createProxy(target, path = '') {
      if (!shouldProxy(target)) {
        return target; // Return original object if it shouldn't be proxied
      }

      return new Proxy(target, {
        set(obj, prop, value) {
          const fullPath = path ? `${path}.${prop}` : prop;
          const startTime = performance.now();
          const oldValue = obj[prop];

          const result = Reflect.set(obj, prop, value);

          const endTime = performance.now();
          
          if (self.isEnabled && oldValue !== value) {
            self.recordMutation({
              storeId,
              type: 'direct',
              property: fullPath,
              oldValue: self.cloneValue(oldValue),
              newValue: self.cloneValue(value),
              duration: endTime - startTime,
              timestamp: Date.now(),
              stack: self.getCallStack()
            });
          }

          return result;
        },

        get(obj, prop) {
          const value = Reflect.get(obj, prop);
          
          // Only proxy plain objects and arrays
          if (shouldProxy(value)) {
            const fullPath = path ? `${path}.${prop}` : prop;
            return createProxy(value, fullPath);
          }
          
          return value;
        }
      });
    }

    // Replace the state with our proxy (only for plain objects)
    try {
      Object.keys(originalState).forEach(key => {
        if (shouldProxy(originalState[key])) {
          originalState[key] = createProxy(originalState[key], key);
        }
      });
    } catch (error) {
      log.info(1,'Could not wrap state proxy:', error);
    }
  }

  monitorComputedProperties(store, storeId) {
    // Find computed properties (getters)
    const proto = Object.getPrototypeOf(store);
    const descriptors = Object.getOwnPropertyDescriptors(proto);

    for (const [key, descriptor] of Object.entries(descriptors)) {
      if (descriptor.get && typeof descriptor.get === 'function') {
        const originalGetter = descriptor.get;

        // Wrap the getter to measure execution time
        const wrappedGetter = function() {
          const startTime = performance.now();
          const result = originalGetter.call(this);
          const endTime = performance.now();
          const duration = endTime - startTime;

          if (duration > 5) { // Log slow computed properties
            log.info(1,`🐌 Slow computed property: ${storeId}.${key} took ${duration.toFixed(2)}ms`);
          }

          return result;
        };

        Object.defineProperty(store, key, {
          get: wrappedGetter,
          configurable: true
        });
      }
    }
  }

  recordMutation(mutation) {
    this.mutations.push(mutation);

    // Keep only recent mutations
    if (this.mutations.length > 1000) {
      this.mutations = this.mutations.slice(-500);
    }

    // Log frequent mutations
    if (mutation.duration > 10) {
      log.info(1,'🐌 Slow mutation:', mutation);
    }
  }

  cloneState(state) {
    try {
      return JSON.parse(JSON.stringify(state));
    } catch (error) {
      return { error: 'Could not clone state' };
    }
  }

  cloneValue(value) {
    try {
      // Handle special objects that can't be JSON serialized
      if (value instanceof Map) {
        return `Map(${value.size}) {${Array.from(value.entries()).slice(0, 3).map(([k, v]) => `${k}: ${v}`).join(', ')}}`;
      }
      if (value instanceof Set) {
        return `Set(${value.size}) {${Array.from(value).slice(0, 3).join(', ')}}`;
      }
      if (value instanceof Date) {
        return value.toISOString();
      }
      if (value instanceof RegExp) {
        return value.toString();
      }
      if (typeof value === 'function') {
        return `Function: ${value.name || 'anonymous'}`;
      }
      
      return JSON.parse(JSON.stringify(value));
    } catch (error) {
      return String(value);
    }
  }

  diffStates(before, after) {
    const changes = {};
    
    function diff(obj1, obj2, path = '') {
      for (const key in obj2) {
        const currentPath = path ? `${path}.${key}` : key;
        
        if (!(key in obj1)) {
          changes[currentPath] = { type: 'added', value: obj2[key] };
        } else if (obj1[key] !== obj2[key]) {
          if (typeof obj2[key] === 'object' && obj2[key] !== null) {
            diff(obj1[key] || {}, obj2[key], currentPath);
          } else {
            changes[currentPath] = {
              type: 'changed',
              oldValue: obj1[key],
              newValue: obj2[key]
            };
          }
        }
      }
      
      for (const key in obj1) {
        const currentPath = path ? `${path}.${key}` : key;
        if (!(key in obj2)) {
          changes[currentPath] = { type: 'removed', value: obj1[key] };
        }
      }
    }
    
    diff(before, after);
    return changes;
  }

  getCallStack() {
    try {
      throw new Error();
    } catch (e) {
      return e.stack?.split('\n').slice(3, 6).map(line => line.trim()) || [];
    }
  }

  getReport(timeWindow = 10000) { // Last 10 seconds
    const cutoff = Date.now() - timeWindow;
    const recentMutations = this.mutations.filter(m => m.timestamp > cutoff);

    const byStore = {};
    const slowMutations = [];
    let totalMutations = 0;

    recentMutations.forEach(mutation => {
      totalMutations++;
      
      if (!byStore[mutation.storeId]) {
        byStore[mutation.storeId] = { count: 0, totalTime: 0, types: {} };
      }
      
      const storeStats = byStore[mutation.storeId];
      storeStats.count++;
      storeStats.totalTime += mutation.duration;
      
      if (!storeStats.types[mutation.type]) {
        storeStats.types[mutation.type] = 0;
      }
      storeStats.types[mutation.type]++;

      if (mutation.duration > 5) {
        slowMutations.push(mutation);
      }
    });

    return {
      timeWindow: `${timeWindow / 1000}s`,
      totalMutations,
      storeStats: byStore,
      slowMutations: slowMutations.slice(-10),
      avgMutationTime: totalMutations > 0 
        ? (recentMutations.reduce((sum, m) => sum + m.duration, 0) / totalMutations).toFixed(2)
        : 0
    };
  }

  printReport() {
    const report = this.getReport();
    console.group('📊 State Change Performance Report');
    log.info(5,'Summary:', {
      totalMutations: report.totalMutations,
      avgTime: `${report.avgMutationTime}ms`,
      timeWindow: report.timeWindow
    });
    
    if (Object.keys(report.storeStats).length > 0) {
      log.info(5,'\nBy Store:');
      console.table(
        Object.entries(report.storeStats).map(([storeId, stats]) => ({
          store: storeId,
          mutations: stats.count,
          totalTime: `${stats.totalTime.toFixed(2)}ms`,
          avgTime: `${(stats.totalTime / stats.count).toFixed(2)}ms`
        }))
      );
    }

    if (report.slowMutations.length > 0) {
      log.info(5,'\nSlow Mutations:');
      console.table(
        report.slowMutations.map(m => ({
          store: m.storeId,
          type: m.type,
          duration: `${m.duration.toFixed(2)}ms`,
          property: m.property || 'multiple',
          time: new Date(m.timestamp).toLocaleTimeString()
        }))
      );
    }
    
    console.groupEnd();
  }

  enable() { this.isEnabled = true; }
  disable() { this.isEnabled = false; }
  clear() { this.mutations = []; }
}

// Usage
const profiler = new StateChangeProfiler();

// Profile all your stores
// profiler.profileStore(useVideoStore());
// profiler.profileStore(useInteractionStore());

// Check reports periodically
// setInterval(() => profiler.printReport(), 10000);

// Expose for debugging
window.stateProfiler = profiler;

export { StateChangeProfiler };